// Vanilla port of FlightSearchPanel.tsx — operates on [data-flight-search-panel].

import { flightResultsHref } from './flightLandingPages.js';
import { trackFlightSearchSubmitted } from './flightSearchAnalytics.js';

function initPanel(root) {
  const regionPills = root.querySelector('[data-fsp-region-pills]');
  const chipsEl = root.querySelector('[data-fsp-chips]');
  const hintEl = root.querySelector('[data-fsp-hint]');
  const whenPills = root.querySelector('[data-fsp-when-pills]');
  const datesEl = root.querySelector('[data-fsp-dates]');
  const dateFrom = root.querySelector('[data-fsp-date-from]');
  const dateTo = root.querySelector('[data-fsp-date-to]');
  const submitBtn = root.querySelector('[data-fsp-submit]');
  const submitLabel = root.querySelector('[data-fsp-submit-label]');
  const ctaHint = root.querySelector('[data-fsp-cta-hint]');

  const airportNames = {};
  root.querySelectorAll('[data-fsp-airport-names] [data-code]').forEach((el) => {
    airportNames[el.getAttribute('data-code')] = el.getAttribute('data-name');
  });

  const selected = new Set();
  const passengers = { adults: 1, children: 0, infants: 0 };
  let tripValue = root.getAttribute('data-default-trip-type') || 'day_trip';
  let whenMode = 'anytime';

  function groupCodes(btn) {
    return (btn.getAttribute('data-codes') || '').split(',').filter(Boolean);
  }

  function groupState(btn) {
    const codes = groupCodes(btn);
    const count = codes.filter((c) => selected.has(c)).length;
    const full = count === codes.length && codes.length > 0;
    const partial = count > 0 && !full;
    return { count, full, partial, active: count > 0 };
  }

  function renderRegions() {
    root.querySelectorAll('[data-fsp-region]').forEach((btn) => {
      const { count, full, partial, active } = groupState(btn);
      btn.classList.toggle('fs-region-pill--active', full);
      btn.classList.toggle('fs-region-pill--partial', partial);
      const countEl = btn.querySelector('[data-fsp-region-count]');
      if (countEl) {
        if (active) {
          countEl.hidden = false;
          countEl.textContent = String(count);
        } else {
          countEl.hidden = true;
        }
      }
    });
    if (regionPills) {
      regionPills.classList.toggle('fs-region-pills--has-selection', selected.size > 0);
    }
  }

  function renderChips() {
    if (!chipsEl) return;
    if (selected.size === 0) {
      chipsEl.hidden = true;
      chipsEl.innerHTML = '';
      if (hintEl) hintEl.hidden = false;
      return;
    }
    if (hintEl) hintEl.hidden = true;
    chipsEl.hidden = false;
    const parts = [...selected].map((code) => {
      const name = airportNames[code] || code;
      return `<div class="fs-airport-chip" data-chip="${code}">
        <span class="fs-airport-chip__code">${code}</span>
        <span class="fs-airport-chip__name">${name}</span>
        <button type="button" class="fs-airport-chip__remove" data-fsp-remove="${code}" aria-label="Remove ${name}">
          <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12"/></svg>
        </button>
      </div>`;
    });
    parts.push('<button type="button" class="fs-clear-all" data-fsp-clear>Clear all</button>');
    chipsEl.innerHTML = parts.join('');
  }

  function renderPassengers() {
    root.querySelectorAll('[data-fsp-count]').forEach((el) => {
      const key = el.getAttribute('data-fsp-count');
      el.textContent = String(passengers[key]);
    });
    root.querySelectorAll('[data-fsp-step]').forEach((btn) => {
      const type = btn.getAttribute('data-fsp-step');
      const dir = btn.getAttribute('data-dir');
      const min = type === 'adults' ? 1 : 0;
      const val = passengers[type];
      if (dir === 'down') btn.disabled = val <= min;
      else btn.disabled = val >= 9;
    });
  }

  function renderSubmit() {
    const canSearch = selected.size > 0;
    const total = passengers.adults + passengers.children + passengers.infants;
    const paxLabel = `${total} ${total === 1 ? 'passenger' : 'passengers'}`;
    if (submitBtn) {
      submitBtn.disabled = !canSearch;
      submitBtn.classList.toggle('fs-cta-btn--disabled', !canSearch);
    }
    if (submitLabel) {
      submitLabel.textContent = canSearch
        ? `Search Flights · ${paxLabel}`
        : 'Select an airport to get started';
    }
    if (ctaHint) ctaHint.hidden = canSearch;
  }

  function renderAll() {
    renderRegions();
    renderChips();
    renderPassengers();
    renderSubmit();
  }

  function toggleGroup(btn) {
    const codes = groupCodes(btn);
    const { full } = groupState(btn);
    if (full) codes.forEach((c) => selected.delete(c));
    else codes.forEach((c) => selected.add(c));
    renderAll();
  }

  root.querySelectorAll('[data-fsp-region]').forEach((btn) => {
    btn.addEventListener('click', () => toggleGroup(btn));
  });

  if (chipsEl) {
    chipsEl.addEventListener('click', (e) => {
      const remove = e.target.closest('[data-fsp-remove]');
      if (remove) {
        selected.delete(remove.getAttribute('data-fsp-remove'));
        renderAll();
        return;
      }
      if (e.target.closest('[data-fsp-clear]')) {
        selected.clear();
        renderAll();
      }
    });
  }

  root.querySelectorAll('[data-fsp-trip]').forEach((btn) => {
    btn.addEventListener('click', () => {
      root.querySelectorAll('[data-fsp-trip]').forEach((b) => b.classList.remove('fs-trip-pill--active'));
      btn.classList.add('fs-trip-pill--active');
      tripValue = btn.getAttribute('data-value') || 'day_trip';
    });
  });

  root.querySelectorAll('[data-fsp-when]').forEach((btn) => {
    btn.addEventListener('click', () => {
      whenMode = btn.getAttribute('data-fsp-when') || 'anytime';
      root.querySelectorAll('[data-fsp-when]').forEach((b) => b.classList.remove('fs-when-pill--active'));
      btn.classList.add('fs-when-pill--active');
      if (whenPills) whenPills.classList.toggle('fs-when-pills--dates', whenMode === 'dates');
      if (datesEl) datesEl.hidden = whenMode !== 'dates';
    });
  });

  root.querySelectorAll('[data-fsp-step]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const type = btn.getAttribute('data-fsp-step');
      const inc = btn.getAttribute('data-dir') === 'up';
      const min = type === 'adults' ? 1 : 0;
      passengers[type] = inc
        ? Math.min(9, passengers[type] + 1)
        : Math.max(min, passengers[type] - 1);
      renderPassengers();
      renderSubmit();
    });
  });

  if (submitBtn) {
    submitBtn.addEventListener('click', () => {
      if (selected.size === 0) return;
      const extraParams = {};
      if (whenMode === 'dates') {
        extraParams.dateSelect = 'Between Specific Dates';
        if (dateFrom && dateFrom.value) extraParams.departureDate = dateFrom.value;
        if (dateTo && dateTo.value) extraParams.returnDate = dateTo.value;
      }
      const flyFrom = [...selected].join(',');
      trackFlightSearchSubmitted({
        fly_from: flyFrom,
        trip_type: tripValue,
        source: 'flight_search_page',
        submit_pathname: window.location.pathname,
        adults: passengers.adults,
        children: passengers.children,
        infants: passengers.infants,
        departure_date: extraParams.departureDate,
        return_date: extraParams.returnDate,
      });
      window.location.href = flightResultsHref({
        fly_from: flyFrom,
        tripType: tripValue,
        adults: String(passengers.adults),
        children: String(passengers.children),
        infants: String(passengers.infants),
        extraParams,
      });
    });
  }

  const prefill = new URLSearchParams(window.location.search).get('fly_from');
  if (prefill) {
    prefill.split(',').forEach((code) => {
      if (airportNames[code.trim()]) selected.add(code.trim());
    });
  }

  renderAll();
}

export function initFlightSearchPanels() {
  document.querySelectorAll('[data-flight-search-panel]').forEach(initPanel);
}
